/**
 * @fileoverview React Query hooks for notes data fetching including notes with
 * their relationships (tags, categories, etc.) and filtered note collections.
 * Uses factory patterns for DRY filtered hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { apiClient, getNoteTags, getSimilarNotes, Note, Tag } from '@/api';
import type { SimilarNote } from '@/api/note';
import { getBacklinks, getForwardLinks, getUnlinkedMentions } from '@/api/note';
import type { LinkedNote } from '@/api/note';
import type { NotesWithTags } from '../types';
import { useKinds, useCategories, useSubcategories, usePeople, useStatuses } from '@/shared/hooks/useReferenceData';
import { sortByCreatedAtDesc } from '../utils/noteUtils';
import { NOTE_QUERY_KEYS, DEFAULT_PAGINATION } from './constants';
import { PRIORITY_SORT_WEIGHT, type Priority } from '@schema/types';

// =============================================================================
// Type Definitions
// =============================================================================

/** Raw data structure returned by the notes query before transformation */
interface NotesRawData {
  notes: Note[];
  noteTagsMap: Map<string, Tag[]>;
}

/** Data structure returned by the single note query */
interface NoteWithTagsData {
  note: Note | null;
  tags: Tag[];
}

// =============================================================================
// Filtered Notes Hook Factory
// =============================================================================

/** Filter function type for creating filtered note hooks */
type NoteFilterFn = (note: NotesWithTags) => boolean;

/** Sort function type for creating filtered note hooks */
type NoteSortFn = (notes: NotesWithTags[]) => NotesWithTags[];

/**
 * Creates a hook that filters and optionally sorts notes from useNotesWithTags.
 * DRY factory to avoid duplicating filtering logic across multiple hooks.
 * @param filterFn - Function to filter notes
 * @param sortFn - Optional function to sort filtered notes
 */
function createFilteredNotesHook(
  filterFn: NoteFilterFn,
  sortFn?: NoteSortFn
): () => UseQueryResult<NotesWithTags[]> {
  return function useFilteredNotes(): UseQueryResult<NotesWithTags[]> {
    const { data: notes, ...query } = useNotesWithTags();

    let filteredNotes = notes?.filter(filterFn) ?? [];
    if (sortFn) {
      filteredNotes = sortFn(filteredNotes);
    }

    return {
      ...query,
      data: filteredNotes,
    } as UseQueryResult<NotesWithTags[]>;
  };
}

/** Match function for notes filtered by a caller-supplied entity id. */
type EntityNoteMatchFn = (note: NotesWithTags, entityId: string) => boolean;

/**
 * Creates a hook that filters useNotesWithTags by an entity id parameter.
 * Used by area, resource, and project note lists.
 */
function createParameterizedNotesHook(
  match: EntityNoteMatchFn,
  sortFn?: NoteSortFn,
): (entityId: string | undefined) => UseQueryResult<NotesWithTags[]> {
  return function useParameterizedNotes(entityId: string | undefined): UseQueryResult<NotesWithTags[]> {
    const { data: notes, ...query } = useNotesWithTags();

    let filteredNotes = entityId
      ? notes?.filter((note) => match(note, entityId)) ?? []
      : [];
    if (sortFn) {
      filteredNotes = sortFn(filteredNotes);
    }

    return {
      ...query,
      data: filteredNotes,
    } as UseQueryResult<NotesWithTags[]>;
  };
}

/**
 * Kind ID for "Weight Loss Ideas" notes surfaced on the /weight page.
 * Matches https://toolpad.keithwalsh.eu/tables/kind/ki3an54.
 */
export const WEIGHT_IDEAS_KIND_ID = 'ki3an54';

// Filter predicates for filtered hooks
const isBookmarked: NoteFilterFn = (note) => note.is_bookmarked;
const isDailyKind: NoteFilterFn = (note) => note.kindName?.toLowerCase() === 'daily';
const isDocumentationKind: NoteFilterFn = (note) => note.kindName?.toLowerCase() === 'documentation';
const isWeightIdeasKind: NoteFilterFn = (note) => note.kind_id === WEIGHT_IDEAS_KIND_ID;

// =============================================================================
// Core Data Hooks
// =============================================================================

/**
 * Fetches all notes with their tags and related reference data.
 * Combines multiple API calls and creates enriched note objects with
 * status names, person names, kind names, etc.
 */
export function useNotesWithTags(): UseQueryResult<NotesWithTags[]> {
  const { data: statuses } = useStatuses();
  const { data: people } = usePeople();
  const { data: kinds } = useKinds();
  const { data: categories } = useCategories();
  const { data: subcategories } = useSubcategories();

  return useQuery({
    queryKey: NOTE_QUERY_KEYS.notesWithTags,
    queryFn: async () => {
      // Fetch notes and tags
      const [notesResponse, noteTagsResult] = await Promise.all([
        apiClient.getMany<Note>('note', DEFAULT_PAGINATION),
        getNoteTags(),
      ]);

      return {
        notes: notesResponse.items,
        noteTagsMap: noteTagsResult.noteTagsMap,
      };
    },
    staleTime: 1 * 60 * 1000, // 1 minute - notes change more frequently
    select: (data) => {
      // Create lookup maps for related data
      const statusMap = new Map(statuses?.map((s) => [s.id, s.name]));
      const personMap = new Map(people?.map((p) => [p.id, [p.first_name, p.last_name].filter(Boolean).join(' ')]));
      const kindMap = new Map(kinds?.map((k) => [k.id, k.name]));
      const categoryMap = new Map(categories?.map((c) => [c.id, c.name]));
      const subcategoryMap = new Map(subcategories?.map((sc) => [sc.id, sc.name]));

      // Combine notes with their tags and related data
      const notesWithTags: NotesWithTags[] = data.notes
        .filter((note): note is Note & { id: string } => !note.deleted_at && typeof note.id === 'string')
        .map((note) => ({
          ...note,
          tags: data.noteTagsMap.get(note.id) || [],
          statusName: note.status_id ? statusMap.get(note.status_id) : undefined,
          personName: note.person_id ? personMap.get(note.person_id) : undefined,
          kindName: note.kind_id ? kindMap.get(note.kind_id) : undefined,
          categoryName: note.category_id ? categoryMap.get(note.category_id) : undefined,
          subcategoryName: note.subcategory_id ? subcategoryMap.get(note.subcategory_id) : undefined,
        }))
        .sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) {
            return a.is_pinned ? -1 : 1;
          }
          const aPriority = PRIORITY_SORT_WEIGHT[(a.priority ?? 'Medium') as Priority] ?? 4;
          const bPriority = PRIORITY_SORT_WEIGHT[(b.priority ?? 'Medium') as Priority] ?? 4;
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          const aDate = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const bDate = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          return bDate - aDate;
        });

      return notesWithTags;
    },
    enabled: !!(statuses && people && kinds && categories && subcategories),
  });
}

/**
 * Fetches a single note by ID with its tags.
 * Returns null for the note if it's soft deleted.
 */
export function useNote(
  noteId: string | undefined,
  options?: { refetchInterval?: UseQueryOptions<NoteWithTagsData>['refetchInterval'] }
): UseQueryResult<NoteWithTagsData> {
  return useQuery<NoteWithTagsData>({
    queryKey: noteId ? NOTE_QUERY_KEYS.note(noteId) : ['note', undefined],
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      if (!noteId) throw new Error('Note ID is required');
      
      const [note, noteTagsResult] = await Promise.all([
        apiClient.getOne<Note>('note', noteId),
        getNoteTags(noteId),
      ]);

      // Filter out soft deleted notes
      if (note.deleted_at) {
        return {
          note: null,
          tags: [],
        };
      }

      return {
        note,
        tags: noteTagsResult.noteTagsMap.get(noteId) || [],
      };
    },
    enabled: !!noteId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetches the 5 nearest-neighbor notes by embedding cosine distance.
 * Returns an empty array while loading or when no embedding exists.
 */
export function useSimilarNotes(noteId: string | undefined): UseQueryResult<SimilarNote[]> {
  return useQuery({
    queryKey: noteId ? NOTE_QUERY_KEYS.similarNotes(noteId) : ['note', undefined, 'similar'],
    queryFn: async () => {
      if (!noteId) throw new Error('Note ID is required');
      return getSimilarNotes(noteId);
    },
    enabled: !!noteId,
    staleTime: 5 * 60 * 1000, // 5 minutes - similar notes change infrequently
  });
}

// =============================================================================
// Bidirectional Linking Hooks
// =============================================================================

/**
 * Fetches notes that link TO the given note (backlinks / reverse lookup).
 */
export function useBacklinks(noteId: string | undefined): UseQueryResult<LinkedNote[]> {
  return useQuery({
    queryKey: noteId ? NOTE_QUERY_KEYS.backlinks(noteId) : ['note', undefined, 'backlinks'],
    queryFn: async () => {
      if (!noteId) throw new Error('Note ID is required');
      return getBacklinks(noteId);
    },
    enabled: !!noteId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetches notes that the given note links TO (forward links).
 */
export function useForwardLinks(noteId: string | undefined): UseQueryResult<LinkedNote[]> {
  return useQuery({
    queryKey: noteId ? NOTE_QUERY_KEYS.forwardLinks(noteId) : ['note', undefined, 'forward-links'],
    queryFn: async () => {
      if (!noteId) throw new Error('Note ID is required');
      return getForwardLinks(noteId);
    },
    enabled: !!noteId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetches notes that mention the given note's title without an explicit [[…]] link.
 * On-demand only — only fetched when the panel is visible.
 */
export function useUnlinkedMentions(noteId: string | undefined): UseQueryResult<LinkedNote[]> {
  return useQuery({
    queryKey: noteId ? NOTE_QUERY_KEYS.unlinkedMentions(noteId) : ['note', undefined, 'unlinked-mentions'],
    queryFn: async () => {
      if (!noteId) throw new Error('Note ID is required');
      return getUnlinkedMentions(noteId);
    },
    enabled: !!noteId,
    staleTime: 5 * 60 * 1000, // 5 minutes — computed on demand
  });
}

// =============================================================================
// Filtered Notes Hooks (created via factory)
// =============================================================================

/**
 * Fetches only bookmarked notes (raw React Query result).
 * Use useBookmarkedNotes from useBookmarkedNotes.ts for transformed/sorted data.
 * Created via factory pattern for DRY filtering logic.
 */
export const useBookmarkedNotesQuery = createFilteredNotesHook(isBookmarked);

/**
 * Fetches only daily notes (notes with Kind='Daily').
 * Sorted chronologically by creation date (newest first).
 * Created via factory pattern for DRY filtering logic.
 */
export const useDailyNotes = createFilteredNotesHook(isDailyKind, sortByCreatedAtDesc);

/**
 * Fetches only documentation notes (notes with Kind='Documentation').
 * Sorted chronologically by creation date (newest first).
 * Created via factory pattern for DRY filtering logic.
 */
export const useDocumentationNotes = createFilteredNotesHook(isDocumentationKind, sortByCreatedAtDesc);

/**
 * Fetches only weight-loss idea notes (notes with Kind='ki3an54').
 * Sorted chronologically by creation date (newest first).
 * Created via factory pattern for DRY filtering logic.
 */
export const useWeightIdeasNotes = createFilteredNotesHook(isWeightIdeasKind, sortByCreatedAtDesc);

/**
 * Fetches notes filtered by resource_id.
 * Sorted chronologically by creation date (newest first).
 */
export const useResourceNotes = createParameterizedNotesHook(
  (note, resourceId) => note.resource_id === resourceId,
  sortByCreatedAtDesc,
);

/**
 * Fetches notes filtered by area_id.
 * Sorted chronologically by creation date (newest first).
 */
export const useAreaNotes = createParameterizedNotesHook(
  (note, areaId) => note.area_id === areaId,
  sortByCreatedAtDesc,
);

/**
 * Fetches notes filtered by project_id, excluding task notes (those appear on
 * the project Kanban). Preserves the base query's pin/priority/updated order.
 */
export const useProjectNotes = createParameterizedNotesHook(
  (note, projectId) => note.project_id === projectId && note.kindName?.toLowerCase() !== 'task',
);

// =============================================================================
// Re-export Mutations for Backward Compatibility
// =============================================================================

export {
  useToggleNoteFlag,
  useUpdateNote,
  useUpdateNoteTags,
  useDeleteNote,
  type ToggleNoteFlagParams,
  type UpdateNoteParams,
  type UpdateNoteTagsParams,
  type DeleteNoteParams,
} from './useNoteMutations';
